'use client'

import { motion } from 'framer-motion'
import { XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { SparkleIcon } from '@/components/shell-icons'
import { Skeleton } from '@/components/ui/skeleton'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { normalizeFedwatchGrid } from '@/features/data/components/widgets/content/fedwatch/fedwatch-normalize'
import { HOME_ROW, HOME_SOFT_SPRING, LiveDot } from '@/features/home/components/home-motion'
import { HomeSparkline } from '@/features/home/components/home-sparkline'
import {
  closestExpiry,
  clusterNear,
  etfFlowRun,
  fundingChange,
  fundingPerDayUsd,
  type LiquidationBucket,
  liquidationClusters,
  netDeltaUsd,
} from '@/features/home/lib/home-card-highlights'
import type { Datapoint } from '@/features/home/lib/home-datapoints'
import type { AssetExposure } from '@/features/home/lib/home-exposure'
import { fundingAprPercent, positionNotionalUsd } from '@/features/home/lib/home-exposure'
import { useNow } from '@/hooks/use-now'
import { useNumberFormat } from '@/hooks/use-number-format'
import { formatFundingCountdown, nextFundingDate } from '@/lib/next-funding'
import { cn } from '@/lib/utils'
import { useAmberdataAnalyticSnapshot } from '@/service/amberdata/analytics.hook'
import { buildAmberdataAnalyticsRequest } from '@/service/amberdata/analytics.types'
import { useLevelProfileSnapshot } from '@/service/analytics/level-profiles.hook'
import { useChartData } from '@/service/chart.hook'
import { useEtfFundFlows } from '@/service/etf-flow.hook'
import { useFedwatchGrid } from '@/service/fedwatch.hook'
import { useImpliedMove, useIvPercentile, useMaxPain } from '@/service/options-analytics.hook'
import {
  AnalyticsType,
  Exchange,
  type Instrument,
  type Ticker,
} from '@/types/generated/common/types'
import {
  CandleFamily,
  type PutCallRatioList,
  type VolMetrics24hList,
} from '@/types/generated/public_api'

const DAY_MS = 86_400_000

/**
 * Each card reports one plain-English line about what it shows, so the AI is asked about
 * exactly the numbers on screen (home-brief.tsx collects them into the prompt).
 */
const SummaryContext = createContext<(id: string, line: string | null) => void>(() => {})
export const DatapointSummaryProvider = SummaryContext.Provider

/** The AI's one line per card, from the written brief (home-brief.tsx provides it). */
const NoteContext = createContext<(id: string) => string | null>(() => null)
export const DatapointNoteProvider = NoteContext.Provider

/** "Not useful" for the card being rendered (DatapointCard provides it to its CardShell). */
const HideContext = createContext<(() => void) | null>(null)

function useReportSummary(id: string, line: string | null) {
  const report = useContext(SummaryContext)
  useEffect(() => report(id, line), [report, id, line])
}

/** Live tickers that never arrive (an unlisted symbol) shouldn't leave a skeleton forever. */
function useGaveUp(ms = 8000): boolean {
  const [gaveUp, setGaveUp] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setGaveUp(true), ms)
    return () => clearTimeout(timer)
  }, [ms])
  return gaveUp
}

interface CardShellProps {
  datapoint: Datapoint
  title: string
  value: ReactNode
  detail: ReactNode
  tone?: 'up' | 'down' | 'warn' | 'danger'
  isPending?: boolean
  /** What moved in this card's data (e.g. "Funding 8% → 24% a year since yesterday"). */
  changed?: string | null
  /** What that does to the user's positions (e.g. "Your long pays ≈ $3 a day"). */
  forYou?: string | null
  /** A change big enough to light the card up. */
  strong?: boolean
  children?: ReactNode
}

/** The shared datapoint card: asset, what it is, why it was picked, the number, a picture. */
function CardShell({
  datapoint,
  title,
  value,
  detail,
  tone,
  isPending,
  changed,
  forYou,
  strong,
  children,
}: CardShellProps) {
  const t = useTranslations('trade.home.brief')
  const hide = useContext(HideContext)
  const note = useContext(NoteContext)(datapoint.id)
  return (
    <motion.li
      variants={HOME_ROW}
      layout
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.18 } }}
      whileHover={{ y: -2 }}
      transition={HOME_SOFT_SPRING}
      className={cn(
        'group/card bg-foreground/[0.03] hover:bg-foreground/[0.05] border-foreground/10 relative flex min-w-0 flex-col gap-2 rounded-md border p-3 transition-colors',
        strong && !isPending && 'border-shell-accent/45 bg-shell-accent/[0.04]'
      )}
    >
      {hide && (
        <button
          type="button"
          onClick={hide}
          aria-label={t('hide')}
          title={t('hide')}
          className="text-foreground/40 hover:text-foreground hover:bg-foreground/10 absolute top-2 right-2 flex size-5 cursor-pointer items-center justify-center rounded-full opacity-0 transition-[opacity,background-color,color] group-focus-within/card:opacity-100 group-hover/card:opacity-100"
        >
          <XIcon className="size-3" />
        </button>
      )}
      <div className="flex items-center gap-2">
        {datapoint.asset ? (
          <CryptoIcon symbol={datapoint.asset} className="size-4 shrink-0" />
        ) : (
          <span className="bg-foreground/10 flex size-4 shrink-0 items-center justify-center rounded-full text-[9px]">
            $
          </span>
        )}
        <span className="text-foreground/70 min-w-0 truncate text-xs tracking-tight">{title}</span>
        {strong && !isPending && (
          <span className="text-shell-accent ml-auto flex shrink-0 items-center gap-1 pr-5 text-[10px]">
            <LiveDot />
            {t('changedBadge')}
          </span>
        )}
      </div>
      {isPending ? (
        <>
          <Skeleton className="h-7 w-24" />
          <Skeleton className="h-3 w-40" />
        </>
      ) : (
        <>
          <span
            className={cn(
              'text-foreground font-display text-2xl leading-7 tracking-tight',
              tone === 'up' && 'text-chart-mint',
              tone === 'down' && 'text-chart-red',
              tone === 'warn' && 'text-ox-gold',
              tone === 'danger' && 'text-chart-red'
            )}
          >
            {value}
          </span>
          <span className="text-foreground/50 min-h-4 text-[11px] leading-4">{detail}</span>
        </>
      )}
      {children && <div className="pt-1">{children}</div>}
      {!isPending && (changed || forYou || note) && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={HOME_SOFT_SPRING}
          className="border-foreground/10 flex flex-col gap-1 border-t pt-2 text-[11px] leading-4"
        >
          {changed && (
            <p className="text-foreground/75">
              <span className="text-foreground/40">{t('changedLabel')} </span>
              {changed}
            </p>
          )}
          {forYou && <p className="text-shell-accent">{forYou}</p>}
          {note && (
            <p className="text-foreground/70 flex gap-1.5">
              <SparkleIcon className="text-shell-accent mt-0.5 size-3 shrink-0" />
              <span>{note}</span>
            </p>
          )}
        </motion.div>
      )}
      <span className="text-shell-accent bg-shell-accent/10 mt-auto self-start rounded-full px-1.5 py-0.5 text-[10px] whitespace-nowrap">
        {t(`why.${datapoint.why}`, { asset: datapoint.asset ?? '' })}
      </span>
    </motion.li>
  )
}

/** A 0–1 position on a track, with optional extra markers, gliding when it changes. */
function Track({
  value,
  markers = [],
  className,
}: {
  value: number | null
  markers?: { at: number; className: string }[]
  className?: string
}) {
  return (
    <div className={cn('bg-foreground/10 relative h-1.5 rounded-full', className)}>
      {markers.map((marker, index) => (
        <span
          key={index}
          className={cn(
            'absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded-full',
            marker.className
          )}
          style={{ left: `${Math.min(100, Math.max(0, marker.at * 100))}%` }}
        />
      ))}
      {value !== null && (
        <motion.span
          className="bg-foreground absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
          initial={{ left: '0%' }}
          animate={{ left: `${Math.min(100, Math.max(0, value * 100))}%` }}
          transition={HOME_SOFT_SPRING}
        />
      )}
    </div>
  )
}

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Options-implied 1σ move to the next expiry, set against the user's closest liquidation. */
function ImpliedMoveCard({
  datapoint,
  exposure,
}: {
  datapoint: Datapoint
  exposure?: AssetExposure
}) {
  const t = useTranslations('trade.home.brief.cards.impliedMove')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? 'BTC'
  const { data, isPending } = useImpliedMove({
    exchange: Exchange.EXCHANGE_DERIBIT,
    underlying: asset,
  })
  const point = data?.points.find((p) => p.daysToExpiration >= 1) ?? data?.points[0]
  const sigma = point ? Number(point.oneSigma) : null
  const closestLiq = useMemo(() => {
    const distances = (exposure?.positions ?? [])
      .map((p) => p.liquidationDistance)
      .filter((d): d is number => d !== null)
    return distances.length ? Math.min(...distances) * 100 : null
  }, [exposure])
  const inside = sigma !== null && closestLiq !== null && closestLiq < sigma
  const expiry = point ? shortDate(Number(point.expiryMs)) : ''
  const held = Math.abs(netDeltaUsd(exposure))

  useReportSummary(
    datapoint.id,
    sigma !== null
      ? `${asset} options-implied 1σ move to ${expiry}: ±${sigma.toFixed(1)}%${
          point?.oneSigmaUsd ? ` ($${Math.round(Number(point.oneSigmaUsd))})` : ''
        }${closestLiq !== null ? `; my closest ${asset} liquidation is ${closestLiq.toFixed(1)}% away` : ''}`
      : null
  )

  // The track spans 2σ: the shaded band is the expected 1σ range, the marker the liquidation.
  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={sigma !== null ? `±${fmt.number(sigma, { maximumFractionDigits: 1 })}%` : '—'}
      detail={
        inside
          ? t('inside', { liq: fmt.number(closestLiq, { maximumFractionDigits: 1 }) })
          : point
            ? t('detail', {
                date: expiry,
                usd: point.oneSigmaUsd ? fmt.usd(Number(point.oneSigmaUsd)) : '—',
              })
            : t('none')
      }
      tone={inside ? 'danger' : undefined}
      isPending={isPending}
      forYou={
        sigma !== null && held > 0
          ? t('forYou', { usd: fmt.usd((held * sigma) / 100, { sensitive: true }), asset })
          : null
      }
      strong={inside}
    >
      {sigma !== null && (
        <div className="bg-foreground/10 relative h-1.5 overflow-hidden rounded-full">
          <motion.span
            className={cn(
              'absolute inset-y-0 left-0 rounded-full',
              inside ? 'bg-chart-red/60' : 'bg-shell-accent/60'
            )}
            initial={{ width: 0 }}
            animate={{ width: '50%' }}
            transition={HOME_SOFT_SPRING}
          />
          {closestLiq !== null && (
            <span
              className="bg-chart-red absolute inset-y-0 w-0.5"
              style={{ left: `${Math.min(100, (closestLiq / (sigma * 2)) * 100)}%` }}
            />
          )}
        </div>
      )}
    </CardShell>
  )
}

/** The perp's funding: rate, annualised, who pays, and the time to the next settlement. */
function FundingCard({
  datapoint,
  ticker,
  instrument,
  exposure,
}: {
  datapoint: Datapoint
  ticker?: Ticker
  instrument?: Instrument
  exposure?: AssetExposure
}) {
  const t = useTranslations('trade.home.brief.cards.funding')
  const fmt = useNumberFormat()
  const now = useNow(1000)
  const rate = ticker?.fundingRate ? Number(ticker.fundingRate) : null
  const apr = rate !== null ? fundingAprPercent(rate, instrument?.fundingInterval) : null
  const side = exposure?.positions.find((p) => p.position.symbol === datapoint.perp?.symbol)?.side
  const pays = apr !== null && side ? (side === 'long' ? apr > 0 : apr < 0) : null
  const next = nextFundingDate(ticker?.nextFundingTime)
  const gaveUp = useGaveUp()
  const [range] = useState(() => {
    const to = Math.floor(Date.now() / 1000)
    return { from: String(to - 24 * 3600), to: String(to) }
  })
  const { data: history } = useChartData({
    symbol: datapoint.perp?.symbol ?? '',
    exchange: datapoint.perp?.exchange ?? Exchange.EXCHANGE_UNSPECIFIED,
    timeframe: 3600,
    from: range.from,
    to: range.to,
    limit: 24,
    families: [CandleFamily.CANDLE_FAMILY_FUNDING_OI],
  })
  const change = fundingChange(history?.candles?.fundingAprClose ?? [], apr)
  const held = exposure?.positions.find((p) => p.position.symbol === datapoint.perp?.symbol)
  const perDay =
    held && apr !== null
      ? fundingPerDayUsd(positionNotionalUsd(held.position), held.side, apr)
      : null
  const pct = (value: number) => fmt.number(value, { maximumFractionDigits: 1 })

  useReportSummary(
    datapoint.id,
    apr !== null
      ? `${datapoint.perp?.symbol} funding ${(rate! * 100).toFixed(4)}% per period (≈${apr.toFixed(1)}% APR)${
          side ? `; I am ${side}, so I ${pays ? 'pay' : 'receive'} it` : ''
        }`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { symbol: datapoint.perp?.symbol ?? '' })}
      value={apr !== null ? `${fmt.number(apr, { maximumFractionDigits: 1 })}%` : '—'}
      detail={
        apr === null
          ? t('none')
          : [
              pays === null ? t('apr') : t(pays ? 'youPay' : 'youReceive'),
              next ? t('next', { time: formatFundingCountdown(next.getTime() - now) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')
      }
      tone={pays === null ? undefined : pays ? 'warn' : 'up'}
      isPending={!ticker && !gaveUp}
      changed={
        change
          ? t(change.flipped ? 'flipped' : 'changed', {
              from: pct(change.from),
              to: pct(change.to),
            })
          : null
      }
      forYou={
        perDay !== null && Math.abs(perDay) >= 0.01
          ? t(perDay > 0 ? 'payDaily' : 'earnDaily', {
              usd: fmt.usd(Math.abs(perDay), { sensitive: true }),
            })
          : null
      }
      strong={change?.strong ?? false}
    />
  )
}

/** 24h price change and open interest on the asset's perp, over a two-day hourly sparkline. */
function MarketMoveCard({
  datapoint,
  ticker,
  exposure,
}: {
  datapoint: Datapoint
  ticker?: Ticker
  exposure?: AssetExposure
}) {
  const t = useTranslations('trade.home.brief.cards.marketMove')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? ''
  const [range] = useState(() => {
    const to = Math.floor(Date.now() / 1000)
    return { from: String(to - 48 * 3600), to: String(to) }
  })
  const { data } = useChartData({
    symbol: datapoint.perp?.symbol ?? '',
    exchange: datapoint.perp?.exchange ?? Exchange.EXCHANGE_UNSPECIFIED,
    timeframe: 3600,
    from: range.from,
    to: range.to,
    limit: 48,
    families: [CandleFamily.CANDLE_FAMILY_CORE, CandleFamily.CANDLE_FAMILY_ORDER_FLOW],
  })
  // Takers' net buying over the last day (the newest 24 hourly candles).
  const netFlow = useMemo(() => {
    const values = (data?.candles?.netBuyVolumeQuote ?? []).slice(-24)
    return values.length ? values.reduce((sum, value) => sum + (value || 0), 0) : null
  }, [data])
  const closes = useMemo(
    () => (data?.candles?.close ?? []).filter((v) => Number.isFinite(v) && v > 0),
    [data]
  )
  const change = ticker?.priceChangePercent24h ? Number(ticker.priceChangePercent24h) : null
  const oi = ticker?.openInterest ? Number(ticker.openInterest) : null
  const gaveUp = useGaveUp()

  useReportSummary(
    datapoint.id,
    change !== null
      ? `${asset} 24h change ${change >= 0 ? '+' : ''}${change.toFixed(2)}%${
          oi ? `, open interest ${Math.round(oi)}` : ''
        }`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={
        change !== null
          ? `${change >= 0 ? '+' : '−'}${fmt.number(Math.abs(change), { maximumFractionDigits: 2 })}%`
          : '—'
      }
      detail={
        oi
          ? t('oi', { oi: fmt.number(oi, { notation: 'compact', maximumFractionDigits: 2 }) })
          : t('none')
      }
      tone={change === null ? undefined : change >= 0 ? 'up' : 'down'}
      isPending={!ticker && !gaveUp}
      changed={
        netFlow !== null && netFlow !== 0
          ? t(netFlow > 0 ? 'boughtFlow' : 'soldFlow', {
              usd: fmt.number(Math.abs(netFlow), {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                maximumFractionDigits: 1,
              }),
            })
          : null
      }
      forYou={
        change !== null && netDeltaUsd(exposure) !== 0
          ? t('forYou', {
              usd: `${(netDeltaUsd(exposure) * change) / 100 >= 0 ? '+' : '−'}${fmt.usd(
                Math.abs((netDeltaUsd(exposure) * change) / 100),
                { sensitive: true }
              )}`,
              asset,
            })
          : null
      }
      strong={change !== null && Math.abs(change) >= 5}
    >
      {closes.length >= 2 && (
        <div className="h-10">
          <HomeSparkline values={closes} drawKey={datapoint.id} />
        </div>
      )}
    </CardShell>
  )
}

/** Today's 30-day ATM implied vol and where it sits in its last 90 days. */
function IvPercentileCard({
  datapoint,
  exposure,
}: {
  datapoint: Datapoint
  exposure?: AssetExposure
}) {
  const t = useTranslations('trade.home.brief.cards.ivPercentile')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? 'BTC'
  const { data, isPending } = useIvPercentile({
    exchange: Exchange.EXCHANGE_DERIBIT,
    underlying: asset,
  })
  const point =
    data?.points.find((p) => p.daysToExpiration === 30) ??
    data?.points[Math.floor((data?.points.length ?? 0) / 2)]
  const iv = point ? Number(point.current) : null
  const rank = point ? Number(point.percentileRank) : null

  useReportSummary(
    datapoint.id,
    iv !== null && rank !== null
      ? `${asset} ${point?.daysToExpiration}d ATM implied vol ${iv.toFixed(1)}%, higher than ${rank.toFixed(0)}% of the last 90 days`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={iv !== null ? `${fmt.number(iv, { maximumFractionDigits: 1 })}%` : '—'}
      detail={
        rank !== null
          ? t(rank >= 80 ? 'rich' : rank <= 20 ? 'cheap' : 'normal', { rank: Math.round(rank) })
          : t('none')
      }
      tone={rank === null ? undefined : rank >= 80 ? 'warn' : undefined}
      isPending={isPending}
      forYou={
        rank !== null && exposure?.hasOption && (rank >= 80 || rank <= 20)
          ? t(rank >= 80 ? 'forYouRich' : 'forYouCheap')
          : null
      }
      strong={rank !== null && Boolean(exposure?.hasOption) && (rank >= 80 || rank <= 20)}
    >
      {rank !== null && (
        <Track value={rank / 100} markers={[{ at: 0.5, className: 'bg-foreground/30' }]} />
      )}
    </CardShell>
  )
}

/** The next expiry's max-pain strike against spot. */
function MaxPainCard({ datapoint }: { datapoint: Datapoint }) {
  const t = useTranslations('trade.home.brief.cards.maxPain')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? 'BTC'
  const { data, isPending } = useMaxPain({ exchange: Exchange.EXCHANGE_DERIBIT, underlying: asset })
  const point = data?.points.find((p) => Number(p.expiryMs) > Date.now()) ?? data?.points[0]
  const strike = point ? Number(point.maxPainStrike) : null
  const spot = data?.underlyingPrice ? Number(data.underlyingPrice) : null
  const gap = strike && spot ? ((strike - spot) / spot) * 100 : null

  useReportSummary(
    datapoint.id,
    strike !== null
      ? `${asset} max pain for ${point ? shortDate(Number(point.expiryMs)) : ''} expiry: ${strike}${
          gap !== null ? ` (${gap >= 0 ? '+' : ''}${gap.toFixed(1)}% from spot)` : ''
        }`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={strike !== null ? fmt.number(strike, { maximumFractionDigits: 0 }) : '—'}
      detail={
        point && gap !== null
          ? t('detail', {
              date: shortDate(Number(point.expiryMs)),
              gap: `${gap >= 0 ? '+' : '−'}${fmt.number(Math.abs(gap), { maximumFractionDigits: 1 })}%`,
            })
          : t('none')
      }
      isPending={isPending}
    />
  )
}

/** Spot ETF net flows: the last day, and the last two weeks as bars. */
function EtfFlowsCard({ datapoint, exposure }: { datapoint: Datapoint; exposure?: AssetExposure }) {
  const t = useTranslations('trade.home.brief.cards.etfFlows')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? 'BTC'
  const [range] = useState(() => {
    const to = Date.now()
    return { from: String(to - 16 * DAY_MS), to: String(to) }
  })
  const { data, isPending } = useEtfFundFlows({
    asset,
    from: range.from,
    to: range.to,
    byFund: false,
  })
  const points = useMemo(
    () => [...(data?.points ?? [])].sort((a, b) => Number(a.date) - Number(b.date)).slice(-14),
    [data]
  )
  const last = points[points.length - 1]
  const week = points.slice(-5).reduce((sum, p) => sum + p.fundFlow, 0)
  const peak = Math.max(1, ...points.map((p) => Math.abs(p.fundFlow)))
  const compact = (v: number) =>
    `${v >= 0 ? '+' : '−'}$${fmt.number(Math.abs(v), { notation: 'compact', maximumFractionDigits: 1 })}`
  const run = etfFlowRun(points.map((point) => point.fundFlow))
  const net = netDeltaUsd(exposure)

  useReportSummary(
    datapoint.id,
    last
      ? `${asset} spot ETF net flow ${compact(last.fundFlow)} on ${shortDate(Number(last.date))}, ${compact(week)} over the last 5 days`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={last ? compact(last.fundFlow) : '—'}
      detail={last ? t('detail', { week: compact(week) }) : t('none')}
      tone={last ? (last.fundFlow >= 0 ? 'up' : 'down') : undefined}
      isPending={isPending}
      changed={
        run ? t(`${run.kind}${run.direction === 'in' ? 'In' : 'Out'}`, { days: run.days }) : null
      }
      forYou={
        run && net !== 0
          ? t((run.direction === 'in') === net > 0 ? 'withYou' : 'againstYou', { asset })
          : null
      }
      strong={run?.kind === 'flipped'}
    >
      {points.length > 0 && (
        // Inflows grow up from the zero line, outflows down from it.
        <div className="relative flex h-10 gap-0.5">
          <span className="bg-foreground/15 absolute inset-x-0 top-1/2 h-px" aria-hidden="true" />
          {points.map((point, index) => {
            const size = `${Math.max(6, (Math.abs(point.fundFlow) / peak) * 100)}%`
            return (
              <div key={point.date} className="flex flex-1 flex-col">
                <div className="flex flex-1 items-end">
                  {point.fundFlow >= 0 && (
                    <motion.span
                      className="bg-chart-mint/70 w-full rounded-t-[1px]"
                      initial={{ height: 0 }}
                      animate={{ height: size }}
                      transition={{ ...HOME_SOFT_SPRING, delay: index * 0.02 }}
                    />
                  )}
                </div>
                <div className="flex flex-1 items-start">
                  {point.fundFlow < 0 && (
                    <motion.span
                      className="bg-chart-red/70 w-full rounded-b-[1px]"
                      initial={{ height: 0 }}
                      animate={{ height: size }}
                      transition={{ ...HOME_SOFT_SPRING, delay: index * 0.02 }}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </CardShell>
  )
}

/** The next FOMC meeting's most likely outcome and its probability. */
function FedwatchCard({ datapoint }: { datapoint: Datapoint }) {
  const t = useTranslations('trade.home.brief.cards.fedwatch')
  const fmt = useNumberFormat()
  const { data, isPending } = useFedwatchGrid({ enableStream: false })
  const grid = useMemo(() => normalizeFedwatchGrid(data), [data])
  const next = grid.rows.find((row) => row.meetingMs >= Date.now() - DAY_MS) ?? grid.rows[0]
  const mode = next?.modeBp ?? null
  const probability = next && mode !== null ? (next.cells.get(mode) ?? null) : null
  const move = mode !== null && grid.standingLowerBp !== null ? mode - grid.standingLowerBp : null
  const outcome =
    move === null
      ? null
      : move === 0
        ? t('hold')
        : move < 0
          ? t('cut', { bp: -move })
          : t('hike', { bp: move })

  useReportSummary(
    datapoint.id,
    outcome && probability !== null && next
      ? `Next FOMC (${next.meetingDate}): ${outcome} priced at ${(probability * 100).toFixed(0)}%`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title')}
      value={outcome ?? '—'}
      detail={
        probability !== null && next
          ? t('detail', {
              probability: fmt.number(probability * 100, { maximumFractionDigits: 0 }),
              date: shortDate(next.meetingMs),
            })
          : t('none')
      }
      isPending={isPending}
    >
      {probability !== null && (
        <div className="bg-foreground/10 h-1.5 overflow-hidden rounded-full">
          <motion.span
            className="bg-shell-accent block h-full rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${probability * 100}%` }}
            transition={HOME_SOFT_SPRING}
          />
        </div>
      )}
    </CardShell>
  )
}

/** Options positioning: the 25-delta risk reversal about a month out, and the put/call ratio. */
function SkewCard({ datapoint, exposure }: { datapoint: Datapoint; exposure?: AssetExposure }) {
  const t = useTranslations('trade.home.brief.cards.skew')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? 'BTC'
  const { data, isPending } = useAmberdataAnalyticSnapshot(
    buildAmberdataAnalyticsRequest({
      symbol: asset,
      analyticsType: AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR,
    })
  )
  const { data: ratioData } = useAmberdataAnalyticSnapshot(
    buildAmberdataAnalyticsRequest({
      symbol: asset,
      analyticsType: AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO,
    })
  )
  const row = closestExpiry((data as VolMetrics24hList | undefined)?.items ?? [])
  const rr = row?.riskReversal25 != null ? Number(row.riskReversal25) : null
  const rrChange = row?.riskReversal25Change != null ? Number(row.riskReversal25Change) : null
  const latestRatio = [...((ratioData as PutCallRatioList | undefined)?.items ?? [])].sort(
    (a, b) => Number(b.timestampMs) - Number(a.timestampMs)
  )[0]
  const pcr =
    latestRatio?.putCallRatioOpenInterest != null
      ? Number(latestRatio.putCallRatioOpenInterest)
      : null
  const net = netDeltaUsd(exposure)
  const vol = (value: number) => fmt.number(Math.abs(value), { maximumFractionDigits: 1 })

  useReportSummary(
    datapoint.id,
    rr !== null && row
      ? `${asset} 25-delta risk reversal (~${Math.round(Number(row.daysToExpiration))}d): ${rr.toFixed(2)} vol${
          rrChange !== null ? `, ${rrChange >= 0 ? '+' : ''}${rrChange.toFixed(2)} in 24h` : ''
        }${pcr !== null ? `; put/call open interest ratio ${pcr.toFixed(2)}` : ''}`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={rr !== null ? `${rr >= 0 ? '+' : '−'}${vol(rr)}` : '—'}
      detail={
        rr === null
          ? t('none')
          : [
              t(rr < 0 ? 'puts' : 'calls'),
              pcr !== null
                ? t('pcr', { pcr: fmt.number(pcr, { maximumFractionDigits: 2 }) })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')
      }
      tone={rr === null ? undefined : rr <= -2 ? 'down' : rr >= 2 ? 'up' : undefined}
      isPending={isPending}
      changed={
        rrChange !== null && Math.abs(rrChange) >= 1
          ? t(rrChange < 0 ? 'towardPuts' : 'towardCalls', { delta: vol(rrChange) })
          : null
      }
      forYou={
        rr !== null && net > 0 && rr <= -2
          ? t('forYouLong')
          : rr !== null && net < 0 && rr >= 2
            ? t('forYouShort')
            : null
      }
      strong={rrChange !== null && Math.abs(rrChange) >= 2}
    >
      {rr !== null && (
        <Track
          value={Math.max(0, Math.min(1, (rr + 10) / 20))}
          markers={[{ at: 0.5, className: 'bg-foreground/30' }]}
        />
      )}
    </CardShell>
  )
}

/**
 * Where liquidations pile up (Hyperliquid's predicted levels): the biggest pool of longs below
 * the price and shorts above it, as a small profile — and whether the user's own liquidation
 * sits on one, where a cascade could reach it.
 */
function LiquidationLevelsCard({
  datapoint,
  exposure,
}: {
  datapoint: Datapoint
  exposure?: AssetExposure
}) {
  const t = useTranslations('trade.home.brief.cards.liquidationLevels')
  const fmt = useNumberFormat()
  const asset = datapoint.asset ?? 'BTC'
  const { data, isPending } = useLevelProfileSnapshot({
    exchange: Exchange.EXCHANGE_HYPERLIQUID,
    symbol: `${asset}-PERP`,
    analyticsType: AnalyticsType.ANALYTICS_TYPE_LIQUIDATION_LEVELS,
  })
  const profile = data?.status === 'published' ? data.profile : null
  const mid = profile && profile.mid > 0 ? profile.mid : null
  const buckets = useMemo<LiquidationBucket[]>(
    () =>
      profile
        ? profile.longLiquidationLevelNotional.map((long, index) => ({
            mid: profile.priceMin + (index + 0.5) * profile.bucketWidth,
            long: Number(long) || 0,
            short: Number(profile.shortLiquidationLevelNotional[index]) || 0,
          }))
        : [],
    [profile]
  )
  const { below, above, nearby } = useMemo(
    () => (mid ? liquidationClusters(buckets, mid) : { below: null, above: null, nearby: [] }),
    [buckets, mid]
  )
  // The user's liquidation closest to the price.
  const myLiquidation = useMemo(() => {
    const prices = (exposure?.positions ?? [])
      .map((risk) => Number(risk.position.liquidationPrice))
      .filter((price) => price > 0)
    if (!mid || prices.length === 0) return null
    return prices.reduce((best, price) =>
      Math.abs(price - mid) < Math.abs(best - mid) ? price : best
    )
  }, [exposure, mid])
  const near = clusterNear(myLiquidation, [below, above])
  const biggest = [below, above]
    .filter((cluster) => cluster !== null)
    .sort((a, b) => b!.usd - a!.usd)[0]
  const compactUsd = (value: number) =>
    fmt.number(value, {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    })
  const peak = Math.max(1, ...nearby.map((bucket) => Math.max(bucket.long, bucket.short)))

  useReportSummary(
    datapoint.id,
    mid && (below || above)
      ? `${asset} predicted liquidations near ${Math.round(mid)}: ${
          below
            ? `$${Math.round(below.usd)} of longs at ${Math.round(below.price)} (${below.distance.toFixed(1)}%)`
            : 'no long cluster'
        }; ${above ? `$${Math.round(above.usd)} of shorts at ${Math.round(above.price)} (+${above.distance.toFixed(1)}%)` : 'no short cluster'}${
          myLiquidation ? `; my closest liquidation is at ${Math.round(myLiquidation)}` : ''
        }`
      : null
  )

  return (
    <CardShell
      datapoint={datapoint}
      title={t('title', { asset })}
      value={biggest ? fmt.number(biggest.price, { maximumSignificantDigits: 5 }) : '—'}
      detail={
        biggest
          ? t(biggest === below ? 'longs' : 'shorts', {
              usd: compactUsd(biggest.usd),
              distance: `${biggest.distance >= 0 ? '+' : '−'}${fmt.number(Math.abs(biggest.distance), { maximumFractionDigits: 1 })}%`,
            })
          : t('none')
      }
      isPending={isPending}
      forYou={
        near && myLiquidation
          ? t('nearYours', {
              price: fmt.number(myLiquidation, { maximumSignificantDigits: 5 }),
              usd: compactUsd(near.usd),
            })
          : null
      }
      strong={Boolean(near)}
    >
      {nearby.length > 0 && mid && (
        // Longs (liquidated below the price) in red, shorts (above) in green; the line is the price.
        <div className="relative flex h-10 items-end gap-px">
          {nearby.map((bucket, index) => {
            const isLong = bucket.mid < mid
            const size = (isLong ? bucket.long : bucket.short) / peak
            return (
              <motion.span
                key={bucket.mid}
                className={cn(
                  'min-w-0 flex-1 rounded-t-[1px]',
                  isLong ? 'bg-chart-red/60' : 'bg-chart-mint/60',
                  myLiquidation &&
                    Math.abs(bucket.mid - myLiquidation) <= profile!.bucketWidth &&
                    'bg-ox-gold'
                )}
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(3, size * 100)}%` }}
                transition={{ ...HOME_SOFT_SPRING, delay: index * 0.01 }}
              />
            )
          })}
          <span
            className="bg-foreground/60 absolute inset-y-0 w-px"
            style={{
              left: `${((mid - nearby[0].mid) / (nearby[nearby.length - 1].mid - nearby[0].mid || 1)) * 100}%`,
            }}
            aria-hidden="true"
          />
        </div>
      )}
    </CardShell>
  )
}

export interface DatapointCardProps {
  datapoint: Datapoint
  exposure?: AssetExposure
  ticker?: Ticker
  instrument?: Instrument
  /** "Not useful": the brief stops picking this kind of datapoint. */
  onHide?: () => void
}

export function DatapointCard({ onHide, ...props }: DatapointCardProps) {
  return (
    <HideContext.Provider value={onHide ?? null}>
      <DatapointCardBody {...props} />
    </HideContext.Provider>
  )
}

function DatapointCardBody({
  datapoint,
  exposure,
  ticker,
  instrument,
}: Omit<DatapointCardProps, 'onHide'>) {
  switch (datapoint.kind) {
    case 'impliedMove':
      return <ImpliedMoveCard datapoint={datapoint} exposure={exposure} />
    case 'funding':
      return (
        <FundingCard
          datapoint={datapoint}
          ticker={ticker}
          instrument={instrument}
          exposure={exposure}
        />
      )
    case 'marketMove':
      return <MarketMoveCard datapoint={datapoint} ticker={ticker} exposure={exposure} />
    case 'ivPercentile':
      return <IvPercentileCard datapoint={datapoint} exposure={exposure} />
    case 'maxPain':
      return <MaxPainCard datapoint={datapoint} />
    case 'etfFlows':
      return <EtfFlowsCard datapoint={datapoint} exposure={exposure} />
    case 'fedwatch':
      return <FedwatchCard datapoint={datapoint} />
    case 'skew':
      return <SkewCard datapoint={datapoint} exposure={exposure} />
    case 'liquidationLevels':
      return <LiquidationLevelsCard datapoint={datapoint} exposure={exposure} />
  }
}
